import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { authenticatedApiPost } from '../../utils/apiWithAuth';
import '../PersonaSelectionModal/PersonaSelectionModal.css';

interface SystemPromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (originalSystemPrompt: string, extractedPersona: string, welcomeMessage?: string, curatedSystemPrompt?: string) => void;
  currentPrompt?: string;
  hasExistingConversation?: boolean;
}

const SystemPromptDialog: React.FC<SystemPromptDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  currentPrompt = '',
  hasExistingConversation = false
}) => {
  const [selectedPrompt, setSelectedPrompt] = useState(currentPrompt);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedPrompt(currentPrompt);
      setCustomPrompt(currentPrompt);
      setIsCustom(Boolean(currentPrompt) && !PRESET_PROMPTS.includes(currentPrompt));
    }
  }, [isOpen, currentPrompt]);

  const PRESET_PROMPTS = [
    'Software System Architect',
    'Code Reviewer',
    'Project Manager',
    'Technical Writer',
    'DevOps Engineer',
    'UI/UX Designer',
    'Data Scientist',
    'Security Expert'
  ];

  const handlePresetSelect = (prompt: string) => {
    setSelectedPrompt(prompt);
    setCustomPrompt(prompt);
    setIsCustom(false);
  };

  const handleCustomToggle = () => {
    setIsCustom(!isCustom);
    if (!isCustom) {
      setSelectedPrompt(customPrompt);
    }
  };

  const handleCustomPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomPrompt(value);
    setSelectedPrompt(value);
  };

  const handleSave = async () => {
    const finalPrompt = isCustom ? customPrompt : selectedPrompt;
    if (!finalPrompt.trim()) return;

    setIsLoading(true);
    try {
      let data;
      
      if (hasExistingConversation) {
        // For existing conversations, use the persona_switch endpoint
        // This will extract the persona and generate a response to the persona change
        const response = await authenticatedApiPost('PERSONA_SWITCH', {
          messages: [{ role: 'user', content: 'Hello' }],
          systemPrompt: finalPrompt
        });

        if (!response.ok) {
          throw new Error('Failed to process persona switch');
        }

        data = await response.json();
  
        
        // For persona switch, we get the extracted persona, response, and curated system prompt
        onSave(finalPrompt, data.extractedPersona, data.response || undefined, data.curatedSystemPrompt);
      } else {
        // For new conversations, use the regular persona endpoint
        const response = await authenticatedApiPost('PERSONA', {
          messages: [{ role: 'user', content: 'Hello' }],
          systemPrompt: finalPrompt
        });

        if (!response.ok) {
          throw new Error('Failed to generate persona');
        }

        data = await response.json();
  
        
        // For new conversations, use the welcome message and curated system prompt
        onSave(finalPrompt, data.extractedPersona, data.response, data.curatedSystemPrompt);
      }
      
      onClose();
    } catch (error) {
      console.error('Error processing persona:', error);
      // Fallback to default values if API call fails
      const welcomeMessage = hasExistingConversation 
        ? `I understand. I am now your ${finalPrompt} assistant. How can I help you continue our conversation?`
        : `Hi, I am JJ, your ${finalPrompt} assistant. How can I help you today?`;
      onSave(finalPrompt, finalPrompt, welcomeMessage, finalPrompt);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedPrompt(currentPrompt);
    setCustomPrompt(currentPrompt);
    setIsCustom(Boolean(currentPrompt) && !PRESET_PROMPTS.includes(currentPrompt));
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="persona-modal-overlay" 
      onClick={handleCancel}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          // Only close on overlay click, not on dialog content
          return;
        }
        e.stopPropagation();
      }}
    >
      <div 
        className="persona-modal-dialog" 
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onDragStart={(e) => e.stopPropagation()}
        onDrag={(e) => e.stopPropagation()}
        onDragEnd={(e) => e.stopPropagation()}
      >
        <header className="persona-modal-header">
          <h2 className="persona-modal-title">Choose Your AI Persona</h2>
          <button className="persona-modal-close" onClick={handleCancel}>&times;</button>
        </header>
        
        <div className="persona-modal-content">
          <div className="persona-notice">
            <svg className="persona-notice-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.21 3.03-1.742 3.03H4.42c-1.532 0-2.492-1.696-1.742-3.03l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="persona-notice-text">
              <strong>Flexible Persona:</strong> You can change your AI persona at any time during the conversation. 
              {hasExistingConversation && ' Changing persona will add a message to inform the AI about the change and preserve your conversation.'}
            </div>
          </div>

          <div className="persona-preset-section">
            <h3 className="persona-section-title">Preset Personas</h3>
            <div className="persona-preset-grid">
              {PRESET_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  className={`persona-preset-option ${selectedPrompt === prompt && !isCustom ? 'persona-preset-selected' : ''}`}
                  onClick={() => handlePresetSelect(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          <div className="persona-custom-section">
            <label className="persona-custom-toggle">
              <input
                type="checkbox"
                checked={isCustom}
                onChange={handleCustomToggle}
                className="persona-custom-checkbox"
              />
              <span className="persona-custom-label">Use Custom Persona</span>
            </label>
            
            {isCustom && (
              <input 
                type="text"
                value={customPrompt}
                onChange={handleCustomPromptChange}
                placeholder="e.g., 'Skeptical Venture Capitalist'"
                className="persona-custom-input"
              />
            )}
          </div>
        </div>

        <footer className="persona-modal-footer">
          <button className="persona-cancel-button" onClick={handleCancel}>
            Cancel
          </button>
          <button 
            className="persona-save-button" 
            onClick={handleSave}
            disabled={!selectedPrompt.trim() || isLoading}
          >
            {isLoading ? 'Generating...' : 'Save Persona'}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default SystemPromptDialog; 